import PageSEO from '@/components/PageSEO';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Debug: Log the backend URL (remove after testing)
console.log('BACKEND_URL:', BACKEND_URL);

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLoadedAt] = useState(() => Date.now());
  const [honeypot, setHoneypot] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Try API first
    if (BACKEND_URL) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/contact`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...formData, website: honeypot, formLoadedAt }),
        });

        if (response.ok) {
          const data = await response.json();
          toast.success('Quote request sent!', {
            description: data.message,
          });
          setFormData({ name: '', email: '', phone: '', message: '' });
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        console.log('API not available, trying Netlify Forms');
      }
    }

    // Fallback to Netlify Forms
    try {
      const formBody = new URLSearchParams({
        'form-name': 'contact',
        'name': formData.name,
        'email': formData.email,
        'phone': formData.phone,
        'message': formData.message,
      }).toString();

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody,
      });

      if (response.ok) {
        toast.success('Quote request sent!', {
          description: "Thank you! We've received your request and will contact you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Final fallback - open email client
      const subject = encodeURIComponent('Quote Request from Website');
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`
      );
      const mailtoLink = `mailto:orders@comalglass.com?subject=${subject}&body=${body}`;
      
      toast.error('Form submission unavailable', {
        description: 'Opening your email client instead...',
      });
      
      // Small delay then open mailto
      setTimeout(() => {
        window.location.href = mailtoLink;
      }, 1500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen">
      <PageSEO title="Contact Us - Free Glass Quote" description="Get a free quote from Comal Glass Company. Custom shower glass, mirrors, wine enclosures, and more. Call (830) 359-9925 or visit us at 25191 State Hwy 46, Bulverde, TX 78163." path="/contact" />
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-6">Get In Touch</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Let's Discuss Your <span className="text-gradient-cyan">Glass Project</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Ready to transform your space? Contact us today for a free consultation and quote.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold">Request a Quote</CardTitle>
                  <p className="text-muted-foreground">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6" data-netlify="true" name="contact">
                    <input type="hidden" name="form-name" value="contact" />
                    {/* Honeypot: invisible to humans, bots fill it out */}
                    <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                      <label htmlFor="website">Website</label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(830) 123-4567"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Project Details *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project..."
                        rows={5}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90 glow-hover"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Submit Request'}
                    </Button>

                    <div className="text-center text-sm text-muted-foreground">
                      <span>Or email us directly at </span>
                      <a 
                        href="mailto:orders@comalglass.com" 
                        className="text-primary hover:underline"
                      >
                        orders@comalglass.com
                      </a>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      By submitting this form, you agree to our privacy policy. We may use the information you submit to communicate with you via phone, text (SMS), and email.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold mb-6">
                  Get in <span className="text-gradient-cyan">Touch</span>
                </h2>
                <p className="text-muted-foreground mb-8">
                  Have questions? Reach out to our team directly. We're here to help bring your vision to life.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">Phone</h3>
                        <a
                          href="tel:8303599925"
                          className="text-muted-foreground hover:text-primary transition-colors"
                        >
                          (830) 359-9925
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1">Email</h3>
                        <a
                          href="mailto:orders@comalglass.com"
                          className="text-muted-foreground hover:text-primary transition-colors break-all"
                        >
                          orders@comalglass.com
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-2">Business Hours</h3>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                          <p>Saturday: By Appointment</p>
                          <p>Sunday: Closed</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Visit Our <span className="text-gradient-cyan">Showroom</span>
            </h2>
            <p className="text-muted-foreground mb-2">
              Come see our glass products and meet our team in person.
            </p>
            <p className="text-lg font-semibold text-foreground">
              25191 State Hwy 46, Bulverde, TX 78163
            </p>
          </div>
          <Card className="border-none shadow-lg overflow-hidden">
            <div className="aspect-video">
              <iframe
                src="https://www.google.com/maps?q=Comal+Glass+Company,+Bulverde,+TX&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Comal Glass Company Location"
              />
            </div>
          </Card>
          <div className="text-center mt-8">
            <a
              href="https://www.google.com/maps/search/?api=1&query=Comal+Glass+Company+Bulverde+TX"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
            >
              <MapPin className="h-5 w-5" />
              <span className="font-medium">Get Directions</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
